import { Box, Button, CssBaseline, Dialog, DialogActions, DialogContent } from "@mui/material";
import { CDashboardConfig, DashboardAppProps, DashboardNotifcation } from "./types";
import { useCallback, useEffect, useState } from "react";
import DashboardEnvContext from "./DashboardEnvContext";
import { Navigate, Route, Routes, useNavigate } from "react-router-dom";
import DashboardModule from "./DashboardModule";
import { componentsMap } from "./ComponentsMap";
import ReactJson from "react-json-view";
import DashboardNotification from "./DashboardNotification";

interface DebugObjectUtilityProps {
	debugObject: unknown | undefined;
	setDebugObject: (debugObject: unknown | undefined) => void;
}

const DebugObjectUtility: React.FC<DebugObjectUtilityProps> = ({ debugObject, setDebugObject }) => {
	const handleClose = () => {
		setDebugObject(undefined);
	};

	return (
		<Dialog onClose={handleClose} open={debugObject != undefined} maxWidth="lg">
			<DialogContent>
				<Box sx={{ maxWidth: "600px", minWidth: "600px", minHeight: "400px" }}>
					<>{debugObject && <ReactJson style={{ width: "100%", height: "100%" }} src={debugObject} />}</>
				</Box>
			</DialogContent>
			<DialogActions>
				<Button onClick={handleClose}>Close</Button>
			</DialogActions>
		</Dialog>
	);
};

const DashboardApp: React.FC<DashboardAppProps> = ({ axios, env, utils, extendedComponents, sidePanelExt, customPages }) => {
	const [data, setData] = useState<CDashboardConfig>();
	const navigate = useNavigate();
	const [debugObject, setDebugObject] = useState<unknown | undefined>();
	const [notification, setNotification] = useState<DashboardNotifcation>();

	useEffect(() => {
		if (extendedComponents) Object.keys(extendedComponents).forEach((c) => (componentsMap[c] = extendedComponents[c]));
		const linkElement = document.createElement("link");
		linkElement.rel = "stylesheet";
		linkElement.href = "https://fonts.googleapis.com/icon?family=Material+Icons";
		document.head.appendChild(linkElement);
		return () => {
			document.head.removeChild(linkElement);
		};
	}, [extendedComponents]);

	const showNotification = useCallback((notification: DashboardNotifcation) => {
		setNotification(notification);
	}, []);

	const showSuccessNotification = useCallback((message: string) => showNotification({
		message,
		severity: 'success',
		duration: 5000
	}), [])

	const showErrorNotification = useCallback((message: string) => showNotification({
		message,
		severity: 'error',
		duration: 8000
	}), [])

	useEffect(() => {
		const fetchData = async () => {
			try {
				const response = await axios.post(`${env["DB_ADMIN_URL_CTRL_PREFIX"]}/backend-action/dashboard/configuration/all`, {});
				const jsonData = await response.data.data;
				setData({ modules: jsonData } as CDashboardConfig);
			} catch (error) {
				console.error("Error fetching data:", error);
			}
			return {} as CDashboardConfig;
		};
		fetchData();
	}, []);

	return (
		<>
			{data && (
				<>
					<DashboardEnvContext.Provider
						value={{
							navigate: navigate,
							config: data,
							axios: axios,
							variables: env,
							utils: {
								...utils,
								debugObject: (debugObject: unknown) => {
									setDebugObject(debugObject);
								},
								showNotification,
								showSuccessNotification,
								showErrorNotification
							},
						}}
					>
						<CssBaseline />
						<>
							{data && (
								<Box sx={{ display: "flex", flex: 1, flexDirection: "column" }}>
									<Routes>
										{data.modules.map((m, index) => (
											<Route key={index} path={"/" + m.path + "/*"} element={<DashboardModule module={m} sidePanelExt={sidePanelExt} customPages={customPages ? customPages.filter((p) => p.moduleId === m.id) : []} />}></Route>
										))}
										<Route path={"*"} element={<Navigate to={"/dashboard/landing"} replace />}></Route>
									</Routes>
								</Box>
							)}
						</>
					</DashboardEnvContext.Provider>
					<DebugObjectUtility debugObject={debugObject} setDebugObject={setDebugObject} />
					<DashboardNotification notification={notification} />
				</>
			)}
		</>
	);
};

export default DashboardApp;
